<template>
  <HrPageLayout
    title="Holiday Statistics"
    subtitle="View holiday and working day statistics"
    icon="📊"
    :breadcrumb-items="[{ label: 'Company Settings' }, { label: 'Holidays', route: '/holidays' }, { label: 'Statistics' }]"
  >
    <template #header-actions>
      <button @click="fetchStatistics" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
    </template>

    <div class="container-fluid">
      <!-- Year selector and summary row -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-2 mb-md-0">Holiday Statistics</h5>
                      <div class="d-flex align-items-center me-auto">
                        <label class="me-2">Year:</label>
                        <select v-model="selectedYear" class="form-select form-select-sm" style="width: 100px;" @change="fetchStatistics">
                          <option v-for="year in yearOptions" :key="year" :value="year">
                            {{ year }}
                          </option>
                        </select>
                      </div>
                      <button @click="fetchStatistics" class="btn btn-sm btn-info">
                        <i class="mdi mdi-refresh me-1"></i> Refresh
                      </button>
                    </div>

                    <div v-if="loading" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading holiday statistics...</p>
                    </div>

                    <div v-else class="row">
                      <div class="col-md-3 col-sm-6 mb-3">
                        <div class="card h-100 border-0 bg-light">
                          <div class="card-body">
                            <div class="d-flex align-items-center">
                              <div class="flex-shrink-0 me-3">
                                <div class="avatar-sm">
                                  <span class="avatar-title bg-primary rounded-circle text-white font-size-18">
                                    <i class="mdi mdi-calendar-check"></i>
                                  </span>
                                </div>
                              </div>
                              <div>
                                <h5 class="text-muted mb-0 font-size-14">Working Days</h5>
                                <h2 class="my-1">{{ statistics.workingDays || 0 }}</h2>
                                <p class="mb-0 text-muted"><small>Out of {{ statistics.totalDays || 365 }} days</small></p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-3 col-sm-6 mb-3">
                        <div class="card h-100 border-0 bg-light">
                          <div class="card-body">
                            <div class="d-flex align-items-center">
                              <div class="flex-shrink-0 me-3">
                                <div class="avatar-sm">
                                  <span class="avatar-title bg-success rounded-circle text-white font-size-18">
                                    <i class="mdi mdi-glass-cocktail"></i>
                                  </span>
                                </div>
                              </div>
                              <div>
                                <h5 class="text-muted mb-0 font-size-14">Total Holidays</h5>
                                <h2 class="my-1">{{ statistics.holidaysOnWorkingDays || 0 }}</h2>
                                <p class="mb-0 text-muted"><small>On working days</small></p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-3 col-sm-6 mb-3">
                        <div class="card h-100 border-0 bg-light">
                          <div class="card-body">
                            <div class="d-flex align-items-center">
                              <div class="flex-shrink-0 me-3">
                                <div class="avatar-sm">
                                  <span class="avatar-title bg-info rounded-circle text-white font-size-18">
                                    <i class="mdi mdi-percent"></i>
                                  </span>
                                </div>
                              </div>
                              <div>
                                <h5 class="text-muted mb-0 font-size-14">Holiday Percentage</h5>
                                <h2 class="my-1">{{ calculateHolidayPercentage() }}%</h2>
                                <p class="mb-0 text-muted"><small>Of working days</small></p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-3 col-sm-6 mb-3">
                        <div class="card h-100 border-0 bg-light">
                          <div class="card-body">
                            <div class="d-flex align-items-center">
                              <div class="flex-shrink-0 me-3">
                                <div class="avatar-sm">
                                  <span class="avatar-title bg-warning rounded-circle text-white font-size-18">
                                    <i class="mdi mdi-calendar-range"></i>
                                  </span>
                                </div>
                              </div>
                              <div>
                                <h5 class="text-muted mb-0 font-size-14">Monthly Average</h5>
                                <h2 class="my-1">{{ calculateMonthlyAverage() }}</h2>
                                <p class="mb-0 text-muted"><small>Holidays per month</small></p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Charts Row -->
            <div class="row">
              <!-- Monthly Holidays Chart -->
              <div class="col-xl-8 col-lg-7 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">Monthly Holiday Distribution</h5>
                    <div class="dropdown">
                      <button class="btn btn-sm btn-light dropdown-toggle" type="button" id="chartViewDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                        {{ chartView }}
                      </button>
                      <ul class="dropdown-menu" aria-labelledby="chartViewDropdown">
                        <li><a class="dropdown-item" href="#" @click.prevent="setChartView('Column Chart')">Column Chart</a></li>
                        <li><a class="dropdown-item" href="#" @click.prevent="setChartView('Line Chart')">Line Chart</a></li>
                      </ul>
                    </div>
                  </div>
                  <div class="card-body">
                    <div v-if="loading" class="text-center py-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                    <div v-else class="monthly-chart-container" style="height: 360px;">
                      <canvas id="monthlyHolidaysChart"></canvas>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Category Distribution Pie Chart -->
              <div class="col-xl-4 col-lg-5 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Holiday Categories</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="loading" class="text-center py-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                    <div v-else-if="!statistics.categoryStats || statistics.categoryStats.length === 0" class="text-center py-5">
                      <i class="mdi mdi-chart-pie font-size-48 text-muted"></i>
                      <p class="mt-2">No category data available</p>
                    </div>
                    <div v-else class="category-chart-container" style="height: 360px;">
                      <canvas id="categoryPieChart"></canvas>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monthly Breakdown Table -->
            <div class="row">
              <div class="col-12 mb-4">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Monthly Breakdown</h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Month</th>
                          <th class="text-center">Working Days</th>
                          <th class="text-center">Holidays</th>
                          <th class="text-center">Net Working Days</th>
                          <th class="text-center">Holiday %</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(month, index) in months" :key="index" :class="{'table-info': hasHighestHolidays(index), 'table-success': hasLowestHolidays(index)}">
                          <td>{{ month }}</td>
                          <td class="text-center">{{ getWorkingDaysForMonth(index) }}</td>
                          <td class="text-center">{{ getHolidaysForMonth(index) }}</td>
                          <td class="text-center">{{ getNetWorkingDaysForMonth(index) }}</td>
                          <td class="text-center">{{ getHolidayPercentageForMonth(index) }}%</td>
                        </tr>
                        </tbody>
                        <tfoot class="table-light">
                        <tr>
                          <th>Total</th>
                          <th class="text-center">{{ statistics.workingDays || 0 }}</th>
                          <th class="text-center">{{ statistics.holidaysOnWorkingDays || 0 }}</th>
                          <th class="text-center">{{ (statistics.workingDays || 0) - (statistics.holidaysOnWorkingDays || 0) }}</th>
                          <th class="text-center">{{ calculateHolidayPercentage() }}%</th>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Working Days Configuration -->
            <div class="row">
              <div class="col-md-6 mb-4">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Working Days Configuration</h5>
                  </div>
                  <div class="card-body">
                    <form @submit.prevent="saveWorkingDaysConfig">
                      <div class="mb-3">
                        <label class="form-label">Select Working Days</label>
                        <div class="d-flex flex-wrap gap-3">
                          <div v-for="day in weekdays" :key="day" class="form-check">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                :id="`day-${day}`"
                                :value="day"
                                v-model="workingDaysConfig"
                            >
                            <label class="form-check-label" :for="`day-${day}`">{{ day }}</label>
                          </div>
                        </div>
                      </div>
                      <button
                          type="submit"
                          class="btn btn-primary"
                          :disabled="configSaving || workingDaysConfig.length === 0"
                      >
                        <span v-if="configSaving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Save Configuration
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-4">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Holiday Policy</h5>
                  </div>
                  <div class="card-body">
                    <p>Your company's holiday policy determines how holidays affect working days. Here's a summary:</p>

                    <ul class="list-group mb-3">
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Total Days in {{ selectedYear }}
                        <span class="badge bg-primary rounded-pill">{{ statistics.totalDays || 365 }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Working Days (Before Holidays)
                        <span class="badge bg-primary rounded-pill">{{ statistics.workingDays || 0 }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Official Holidays on Working Days
                        <span class="badge bg-danger rounded-pill">{{ statistics.holidaysOnWorkingDays || 0 }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center list-group-item-success">
                        Net Working Days
                        <span class="badge bg-success rounded-pill">{{ (statistics.workingDays || 0) - (statistics.holidaysOnWorkingDays || 0) }}</span>
                      </li>
                    </ul>

                    <div class="alert alert-info">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Changes to the working days configuration will affect statistics and calendar displays throughout the system.
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";
import Chart from 'chart.js/auto';

export default {
  name: "HolidayStatistics",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      configSaving: false,
      selectedYear: new Date().getFullYear(),
      chartView: 'Column Chart',
      statistics: {
        totalDays: 0,
        workingDays: 0,
        holidaysOnWorkingDays: 0,
        monthlyHolidays: [],
        monthlyWorkingDays: [],
        monthlyHolidaysOnWorkingDays: [],
        categoryStats: [],
        workingDaysList: []
      },
      monthlyChart: null,
      categoryChart: null,
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      weekdays: [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
      ],
      workingDaysConfig: []
    };
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear();
      return [
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2
      ];
    }
  },
  mounted() {
    this.fetchStatistics();
  },
  methods: {
    fetchStatistics() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getHolidayStatistics");
      data.append("year", this.selectedYear);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.statistics = res.data.data;

              // Set working days config from API data
              this.workingDaysConfig = this.statistics.workingDaysList || [
                'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'
              ];

              // Initialize charts after data is loaded
              this.$nextTick(() => {
                this.initMonthlyChart();
                this.initCategoryChart();
              });
            } else {
              console.error("Error fetching holiday statistics:", res.data.message);
              this.showToast("error", "Failed to load statistics: " + res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching holiday statistics");
          });
    },

    saveWorkingDaysConfig() {
      if (this.workingDaysConfig.length === 0) {
        this.showToast("error", "Please select at least one working day");
        return;
      }

      this.configSaving = true;

      const data = new FormData();
      data.append("function", "updateHolidayConfigurations");
      data.append("settings[working_days]", this.workingDaysConfig.join(','));

      execute(data, authUrl)
          .then((res) => {
            this.configSaving = false;

            if (res.data.success) {
              this.showToast("success", "Working days configuration saved successfully");
              // Refresh statistics to reflect the changes
              this.fetchStatistics();
            } else {
              this.showToast("error", res.data.message || "Failed to save configuration");
            }
          })
          .catch((error) => {
            this.configSaving = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when saving configuration");
          });
    },

    initMonthlyChart() {
      const ctx = document.getElementById('monthlyHolidaysChart').getContext('2d');

      // Destroy existing chart if it exists
      if (this.monthlyChart) {
        this.monthlyChart.destroy();
      }

      // Prepare data
      const labels = this.months;
      const holidaysData = this.statistics.monthlyHolidaysOnWorkingDays || Array(12).fill(0);
      const workingDaysData = this.statistics.monthlyWorkingDays || Array(12).fill(0);

      // Create chart
      this.monthlyChart = new Chart(ctx, {
        type: this.chartView === 'Line Chart' ? 'line' : 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Holidays',
              data: holidaysData,
              backgroundColor: 'rgba(220, 53, 69, 0.7)',
              borderColor: 'rgba(220, 53, 69, 1)',
              borderWidth: 1
            },
            {
              label: 'Working Days',
              data: workingDaysData,
              backgroundColor: 'rgba(0, 123, 255, 0.5)',
              borderColor: 'rgba(0, 123, 255, 1)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Days'
              }
            }
          },
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                footer: (tooltipItems) => {
                  const index = tooltipItems[0].dataIndex;
                  const percentage = this.getHolidayPercentageForMonth(index);
                  return `Holiday Percentage: ${percentage}%`;
                }
              }
            }
          }
        }
      });
    },

    initCategoryChart() {
      if (!this.statistics.categoryStats || this.statistics.categoryStats.length === 0) {
        return;
      }

      const ctx = document.getElementById('categoryPieChart').getContext('2d');

      // Destroy existing chart if it exists
      if (this.categoryChart) {
        this.categoryChart.destroy();
      }

      // Prepare data
      const labels = this.statistics.categoryStats.map(cat => cat.category_name);
      const data = this.statistics.categoryStats.map(cat => cat.count);
      const backgroundColor = this.statistics.categoryStats.map(cat => cat.color_code);

      // Create chart
      this.categoryChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: backgroundColor,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = context.raw || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = Math.round((value / total) * 100);
                  return `${label}: ${value} (${percentage}%)`;
                }
              }
            }
          }
        }
      });
    },

    setChartView(view) {
      this.chartView = view;
      this.initMonthlyChart();
    },

    calculateHolidayPercentage() {
      const workingDays = this.statistics.workingDays || 0;
      const holidays = this.statistics.holidaysOnWorkingDays || 0;

      if (workingDays === 0) return 0;

      return ((holidays / workingDays) * 100).toFixed(1);
    },

    calculateMonthlyAverage() {
      const holidays = this.statistics.holidaysOnWorkingDays || 0;
      return (holidays / 12).toFixed(1);
    },

    getWorkingDaysForMonth(monthIndex) {
      const monthlyData = this.statistics.monthlyWorkingDays || [];
      return monthlyData[monthIndex] || 0;
    },

    getHolidaysForMonth(monthIndex) {
      const monthlyData = this.statistics.monthlyHolidaysOnWorkingDays || [];
      return monthlyData[monthIndex] || 0;
    },

    getNetWorkingDaysForMonth(monthIndex) {
      const workingDays = this.getWorkingDaysForMonth(monthIndex);
      const holidays = this.getHolidaysForMonth(monthIndex);
      return workingDays - holidays;
    },

    getHolidayPercentageForMonth(monthIndex) {
      const workingDays = this.getWorkingDaysForMonth(monthIndex);
      const holidays = this.getHolidaysForMonth(monthIndex);

      if (workingDays === 0) return 0;

      return ((holidays / workingDays) * 100).toFixed(1);
    },

    hasHighestHolidays(monthIndex) {
      if (!this.statistics.monthlyHolidaysOnWorkingDays) return false;

      const holidaysData = this.statistics.monthlyHolidaysOnWorkingDays;
      const maxHolidays = Math.max(...holidaysData);

      if (maxHolidays === 0) return false;

      return holidaysData[monthIndex] === maxHolidays;
    },

    hasLowestHolidays(monthIndex) {
      if (!this.statistics.monthlyHolidaysOnWorkingDays) return false;

      const holidaysData = this.statistics.monthlyHolidaysOnWorkingDays;

      // Filter out zero values (might be months with no working days)
      const nonZeroHolidays = holidaysData.filter(h => h > 0);

      if (nonZeroHolidays.length === 0) return false;

      const minHolidays = Math.min(...nonZeroHolidays);

      // Only highlight if there are any holidays in this month
      return holidaysData[monthIndex] === minHolidays && holidaysData[monthIndex] > 0;
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  beforeUnmount() {
    // Clean up chart instances
    if (this.monthlyChart) {
      this.monthlyChart.destroy();
    }

    if (this.categoryChart) {
      this.categoryChart.destroy();
    }
  }
};
</script>

<style scoped>
.card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.monthly-chart-container, .category-chart-container {
  position: relative;
  width: 100%;
}
</style>